import React, { useEffect, useState } from "react";
import axios from "axios";
import { Lock, Globe2, Users, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VISIBILITY = {
  private: { label: "Private", icon: Lock, hint: "Only you can see this project and its chats." },
  organization: { label: "Organization", icon: Globe2, hint: "Everyone in your organization / tenant can see it." },
  members: { label: "Specific members", icon: Users, hint: "Only you and the members you pick." },
};

// Create form (mode="create") or sharing editor (mode="edit") for a Workspace project.
export default function ProjectSharing({ mode, project, busy, onSubmit, onCancel }) {
  const [name, setName] = useState(project?.name || "");
  const [visibility, setVisibility] = useState(project?.visibility in VISIBILITY ? project.visibility : (project ? "organization" : "private"));
  const [memberIds, setMemberIds] = useState(project?.member_ids || []);
  const [instructions, setInstructions] = useState(project?.instructions || "");
  const [members, setMembers] = useState(null);

  useEffect(() => {
    axios.get(`${API}/workspace/members`).then(({ data }) => setMembers(data.members || [])).catch(() => setMembers([]));
  }, []);
  useEffect(() => {
    if (!project) return;
    setName(project.name || "");
    setVisibility(project.visibility in VISIBILITY ? project.visibility : "organization");
    setMemberIds(project.member_ids || []);
    setInstructions(project.instructions || "");
  }, [project]);

  const toggle = (id) => setMemberIds((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  const submit = () => onSubmit({ name: name.trim(), visibility, member_ids: visibility === "members" ? memberIds : [], instructions });

  return (
    <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-5 space-y-4" data-testid={`project-sharing-${mode}`}>
      <div className="text-sm font-semibold">{mode === "create" ? "New project" : "Sharing & access"}</div>
      <label className="block text-xs text-[var(--qn-foreground-muted)]">
        Project name
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Nordic Roaming 2026"
          className="mt-1 w-full rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] px-3 py-2 text-sm text-[var(--qn-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--qn-primary)]"
          data-testid="project-name-input" />
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Visibility">
        {Object.entries(VISIBILITY).map(([k, v]) => (
          <button key={k} type="button" role="radio" aria-checked={visibility === k} onClick={() => setVisibility(k)}
            data-testid={`project-visibility-${k}`}
            className={`text-left rounded-lg border p-3 transition-colors ${visibility === k
              ? "border-[var(--qn-primary)] bg-[var(--qn-primary-muted)]" : "border-[var(--qn-border)] hover:border-[var(--qn-primary)]/40"}`}>
            <div className="flex items-center gap-2 text-sm font-medium"><v.icon className="w-4 h-4" /> {v.label}{visibility === k && <Check className="w-3.5 h-3.5 ml-auto text-[var(--qn-primary)]" />}</div>
            <div className="text-[11px] text-[var(--qn-foreground-muted)] mt-1">{v.hint}</div>
          </button>
        ))}
      </div>
      {visibility === "members" && (
        <div className="rounded-lg border border-[var(--qn-border)] p-3 max-h-48 overflow-y-auto space-y-1" data-testid="project-members-picker">
          {members === null && <Loader2 className="w-4 h-4 animate-spin" />}
          {members?.length === 0 && <div className="text-xs text-[var(--qn-foreground-subtle)]" data-testid="project-members-empty">No other members in your organization yet — invite teammates under Settings › Team.</div>}
          {(members || []).map((m) => (
            <label key={m.user_id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`project-member-${m.user_id}`}>
              <input type="checkbox" checked={memberIds.includes(m.user_id)} onChange={() => toggle(m.user_id)} className="accent-[var(--qn-primary)]" />
              <span className="truncate">{m.name || m.email}</span>
              <span className="text-[11px] text-[var(--qn-foreground-subtle)] truncate">{m.email} · {m.role}</span>
            </label>
          ))}
        </div>
      )}
      <label className="block text-xs text-[var(--qn-foreground-muted)]">
        Project instructions (shared with every chat in the project)
        <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={2} placeholder="Context every chat in this project should know…"
          className="mt-1 w-full rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] px-3 py-2 text-sm text-[var(--qn-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--qn-primary)]"
          data-testid="project-instructions-input" />
      </label>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={busy || !name.trim()} className="gap-1.5" data-testid="project-sharing-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {mode === "create" ? "Create project" : "Save sharing"}
        </Button>
        {onCancel && <Button size="sm" variant="ghost" onClick={onCancel} data-testid="project-sharing-cancel">Cancel</Button>}
      </div>
    </div>
  );
}
